import React from 'react';
import { FIG_INK, FIG_TEXT } from '../figureStyle';

// ─── AI investment share of GDP (tiny at-a-glance sketch) ────────────────────
// For the epoch.ai/data-insights/ai-datacenter-share-gdp link tooltip: US AI
// datacenter investment (hardware + construction + networking) as % of US GDP.
// Solid ink = history from 2018 through mid-2026, anchored to Epoch's insight
// (~0.8% of GDP in Q1 2026, boom starting 2023, small near-flat share before
// that); dashed = the same recent growth rate extrapolated to 2030. Same visual language as the
// simple-takeoff sketches: no axis lines, dashed gridlines, one line of ink.

const YEAR_MIN = 2018;
const YEAR_MAX = 2030;
const NOW = 2026.5;
const SHARE_MAX = 3.4; // % of GDP at the top of the plot

// [year, share % of GDP] — solid history, then dashed extrapolation from NOW.
const SO_FAR: [number, number][] = [
  [2018, 0.05], [2019, 0.06], [2020, 0.08], [2021, 0.1],
  [2022, 0.14], [2022.5, 0.16], [2023, 0.21], [2023.5, 0.28],
  [2024, 0.38], [2024.5, 0.48], [2025, 0.6], [2025.5, 0.71],
  [2026, 0.83], [NOW, 0.95],
];
const PROJECTED: [number, number][] = [
  [NOW, 0.95], [2027, 1.15], [2027.5, 1.42], [2028, 1.72], [2028.5, 2.05],
  [2029, 2.4], [2029.5, 2.72], [2030, 3.08],
];

const VX = -9;
const VW = 312;
const VH = 130;
const PLOT_L = 8;
const PLOT_R = 292;
const PLOT_T = 8;
const PLOT_B = 104;

const LABEL_FS = 8;

const x = (year: number) => PLOT_L + ((year - YEAR_MIN) / (YEAR_MAX - YEAR_MIN)) * (PLOT_R - PLOT_L);
const y = (share: number) => PLOT_B - (share / SHARE_MAX) * (PLOT_B - PLOT_T);
const path = (pts: [number, number][]) =>
  pts.map(([yr, v], i) => `${i === 0 ? 'M' : 'L'}${x(yr).toFixed(2)},${y(v).toFixed(2)}`).join(' ');

export default function SimpleAiInvestment() {
  return (
    <div className="mx-auto mb-4">
      <svg viewBox={`${VX} 0 ${VW} ${VH}`} className="w-full h-auto font-mono">
        {[1, 2, 3].map((pct) => (
          <g key={pct}>
            <line
              x1={PLOT_L} y1={y(pct)} x2={PLOT_R} y2={y(pct)}
              stroke={FIG_INK} strokeWidth={0.75} strokeDasharray="2 3"
              opacity={pct === 1 ? 0.55 : 0.32}
            />
            <text x={PLOT_L - 4} y={y(pct) + 2} textAnchor="end" fontSize={LABEL_FS} fill={FIG_TEXT}>
              {pct}%
            </text>
          </g>
        ))}

        {[2018, 2020, 2022, 2024, 2026, 2028, 2030].map((yr) => (
          <text key={yr} x={x(yr)} y={PLOT_B + 12} textAnchor="middle" fontSize={LABEL_FS} fill={FIG_TEXT}>
            {yr}
          </text>
        ))}

        <path
          d={path(SO_FAR)} fill="none" stroke={FIG_INK}
          strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round"
        />
        <path
          d={path(PROJECTED)} fill="none" stroke={FIG_INK} strokeDasharray="5 4"
          strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round"
        />

        <circle cx={x(NOW)} cy={y(0.95)} r={2.5} fill={FIG_INK} />
        <text x={x(NOW)} y={y(0.95) - 7} textAnchor="middle" fontSize={LABEL_FS} fill={FIG_TEXT} opacity={0.65}>
          now
        </text>
      </svg>
    </div>
  );
}
